import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext, useNavigate } from 'react-router-dom';
import config from '../config';
import {
    Download,
    CreditCard,
    MapPin,
    Loader2,
    AlertCircle,
    Wallet,
    Clock,
    Eye,
    X
} from 'lucide-react';

export default function MyBills() {
    const { selectedProperty } = useOutletContext();
    const navigate = useNavigate();

    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [reportLoading, setReportLoading] = useState(null); // 'ver' | 'descargar' | null
    const [cartola, setCartola] = useState(null); // { url, name } del PDF abierto en el modal
    const [reportError, setReportError] = useState(null);
    const reportAbort = useRef(null);

    // Función para corregir el desfase de fecha UTC (evita que el 29 se vea como 28)
    const parseUTCDate = (dateStr) => {
        if (!dateStr || dateStr.startsWith('0001')) return null;
        const date = new Date(dateStr);
        // Sumamos el desfase de la zona horaria para mantener el día calendario original del JSON
        return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    };

    const getDates = () => {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setMonth(today.getMonth() - 13);
        const formatDate = (date) => date.toISOString().split('T')[0];
        return {
            today: formatDate(today),
            pastDate: formatDate(pastDate)
        };
    };

    const fetchBills = async () => {
        if (!selectedProperty?.nro_cliente) return;

        const token = sessionStorage.getItem('token');
        if (!token) {
            // Datos de prueba basados en tu estructura real
            const mockBills = [
                { id: 'd67ddf62-feba-4aac-bda9-118ec37fd4cd', fecha_vencimiento: '2025-10-29T00:00:00Z', total_factura: 13100, abono: 13100, saldo: 0, desc_estado: 'PAGADO', folio_valido: '542213' }
            ];
            setBills(mockBills);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        const { today, pastDate } = getDates();

        const params = new URLSearchParams({
            startDate: pastDate,
            endDate: today,
            nro_cliente: selectedProperty.nro_cliente
        });

        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-billing?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401) {
                sessionStorage.clear();
                navigate('/login');
                return;
            }

            if (!response.ok) throw new Error(`Error ${response.status}`);

            const data = await response.json();
            const sortedData = (Array.isArray(data) ? data : []).sort((a, b) =>
                new Date(b.fecha_vencimiento || b.fecha_emision) - new Date(a.fecha_vencimiento || a.fecha_emision)
            );
            setBills(sortedData);
        } catch (err) {
            setError("No pudimos conectar con el servicio de facturación.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async (boleta) => {
        const token = sessionStorage.getItem('token');
        if (!token || !boleta?.id) return;

        setDownloadingId(boleta.id);
        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-billing/download?docventa_id=${boleta.id}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("No se pudo descargar el archivo");

            const dateObj = parseUTCDate(boleta.fecha_vencimiento || boleta.fecha_emision);
            const day = dateObj.getDate();
            const month = dateObj.toLocaleDateString('es-CL', { month: 'long' }).toLowerCase();
            const year = dateObj.getFullYear();
            const fileName = `boleta-${day}-${month}-${year}-folio-${boleta.folio_valido}.pdf`;

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading PDF:", err);
        } finally {
            setDownloadingId(null);
        }
    };

    // El servidor arma los PDF en una cola: sin 429/503, pero puede tardar con carga.
    // Abortar (cancelar, cambiar de servicio o salir) libera el turno en la cola.
    const REPORT_TIMEOUT_MS = 120000;

    const handleCartola = async (mode) => {
        if (reportAbort.current) {
            reportAbort.current.abort();
            return;
        }
        const token = sessionStorage.getItem('token');
        if (!token || !selectedProperty?.nro_cliente) return;

        const { today, pastDate } = getDates();
        const nro = selectedProperty.nro_cliente;
        const params = new URLSearchParams({ startDate: pastDate, endDate: today, nro_cliente: nro });

        const controller = new AbortController();
        reportAbort.current = controller;
        let timedOut = false;
        const timer = setTimeout(() => { timedOut = true; controller.abort(); }, REPORT_TIMEOUT_MS);
        setReportLoading(mode);
        setReportError(null);

        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-cartola?${params.toString()}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` },
                signal: controller.signal
            });

            if (response.status === 401) {
                sessionStorage.clear();
                navigate('/login');
                return;
            }
            if (!response.ok) {
                const body = await response.json().catch(() => null);
                throw new Error(body?.error || `Error ${response.status}`);
            }

            // CORS no expone Content-Disposition: el nombre se arma aquí, igual que el del servidor.
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const name = `cartola_${nro}_${pastDate}_${today}.pdf`;
            if (mode === 'ver') {
                setCartola({ url, name });
                return;
            }
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (err.name !== 'AbortError') {
                setReportError(`No se pudo generar la cartola: ${err.message}`);
            } else if (timedOut) {
                setReportError("La cartola tardó demasiado. Intenta nuevamente en unos minutos.");
            }
        } finally {
            clearTimeout(timer);
            if (reportAbort.current === controller) {
                reportAbort.current = null;
                setReportLoading(null);
            }
        }
    };

    // Modal de la cartola: Escape lo cierra; al cerrarlo o salir se libera el PDF.
    useEffect(() => {
        if (!cartola) return;
        const onKey = (e) => e.key === 'Escape' && setCartola(null);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.URL.revokeObjectURL(cartola.url);
        };
    }, [cartola]);

    const handlePayment = () => {
        const userStr = sessionStorage.getItem('user');
        if (!userStr || !selectedProperty?.nro_cliente) return;

        const user = JSON.parse(userStr);
        // Formateamos el RUT (asumiendo que viene como número y DV por separado en el objeto user)
        const rutCompleto = `${user.rut}-${user.dv}`;

        const url = `${config.webpayUrl}?nrut=${rutCompleto}&ncliente=${selectedProperty.nro_cliente}#no-back-button`;
        window.open(url, '_blank');
    };

    useEffect(() => {
        fetchBills();
        setReportError(null);
        setCartola(null);
        return () => reportAbort.current?.abort();
    }, [selectedProperty]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
    };

    const formatMonthYear = (dateStr) => {
        const date = parseUTCDate(dateStr);
        if (!date) return "Mes Actual";
        return date.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    };

    const formatDateShort = (dateStr) => {
        const date = parseUTCDate(dateStr);
        if (!date) return "---";
        return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Calculamos la deuda total sumando el campo "saldo" de cada registro
    const totalDeuda = bills.reduce((acc, bill) => acc + (bill.saldo || 0), 0);

    return (
        <div className="max-w-5xl mx-auto w-full animate-in fade-in duration-500 pb-10">
            {/* Estilos para "Imprimir / PDF": oculta los controles y evita cortar boletas entre páginas */}
            <style>
                {`
                @media print {
                    @page { size: auto; margin: 15mm; }
                    body, html, #root, main, .overflow-y-auto {
                        background: white !important;
                        overflow: visible !important;
                        height: auto !important;
                        position: static !important;
                    }
                    .no-print { display: none !important; }
                    .boleta-card {
                        break-inside: avoid;
                        border: 1px solid #e2e8f0 !important;
                        margin-bottom: 20px !important;
                        box-shadow: none !important;
                    }
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
                `}
            </style>

            <div className="mb-8 lg:mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl lg:text-4xl font-black text-slate-800 mb-2">Mis Boletas</h1>
                    <p className="text-slate-500">Historial de facturación y pagos pendientes.</p>
                </div>

                {bills.length > 0 && (
                    <div className="flex flex-col sm:items-end gap-2 no-print">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => handleCartola('ver')}
                            disabled={reportLoading === 'descargar'}
                            title={reportLoading === 'ver' ? "Cancelar" : "Ver cartola"}
                            className="px-4 py-2.5 bg-white text-sky-600 rounded-xl font-bold border border-slate-100 hover:border-sky-200 transition-all flex items-center justify-center gap-2 text-sm whitespace-nowrap shadow-sm active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                        >
                            {reportLoading === 'ver' ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
                            {reportLoading === 'ver' ? "Generando… (cancelar)" : "Ver cartola"}
                        </button>
                        <button
                            onClick={() => handleCartola('descargar')}
                            disabled={reportLoading === 'ver'}
                            title={reportLoading === 'descargar' ? "Cancelar" : "Descargar cartola en PDF"}
                            className="px-4 py-2.5 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 transition-all flex items-center justify-center gap-2 text-sm whitespace-nowrap shadow-md shadow-sky-100 active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                        >
                            {reportLoading === 'descargar' ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                            {reportLoading === 'descargar' ? "Generando… (cancelar)" : "Descargar cartola"}
                        </button>
                    </div>
                    {reportError && (
                        <p role="alert" className="text-red-500 text-xs font-bold">{reportError}</p>
                    )}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="md:col-span-2 bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col justify-center">
                    <label className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2">
                        <MapPin size={12} className="text-sky-500" /> Servicio Activo
                    </label>
                    <div className="text-slate-700 font-bold text-lg leading-tight">
                        #{selectedProperty?.nro_cliente} <span className="text-slate-300 mx-2">|</span> {selectedProperty?.up_direccion}
                    </div>
                </div>

                <div className="bg-sky-600 p-6 rounded-3xl shadow-lg shadow-sky-100 text-white relative overflow-hidden flex flex-col justify-between">
                    <div className="relative z-10">
                        <label className="text-sky-100/70 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2">
                            <Wallet size={12} /> Total Deuda
                        </label>
                        <div className="text-3xl font-black tracking-tighter">
                            {formatCurrency(totalDeuda)}
                        </div>
                    </div>

                    {totalDeuda > 0 && config.webpayUrl && (
                        <button
                            onClick={handlePayment}
                            className="no-print relative z-10 mt-4 w-full py-2.5 bg-white text-sky-600 rounded-xl font-bold hover:bg-sky-50 transition-all flex items-center justify-center gap-2 text-sm shadow-sm active:scale-95 group"
                        >
                            <CreditCard size={18} className="group-hover:scale-110 transition-transform" />
                            Pagar
                        </button>
                    )}

                    <Wallet size={80} className="absolute -right-4 -bottom-4 text-white/10 rotate-12" />
                </div>
            </div>

            {loading && (
                <div className="flex flex-col items-center justify-center py-24 text-sky-600 no-print">
                    <Loader2 className="animate-spin mb-4" size={48} />
                    <p className="font-bold text-lg">Cargando facturas...</p>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-100 p-8 rounded-3xl flex flex-col items-center text-center gap-4 text-red-700 no-print">
                    <AlertCircle size={40} className="opacity-50" />
                    <div>
                        <p className="font-bold text-lg">Error de conexión</p>
                        <p className="text-sm opacity-80">{error}</p>
                    </div>
                    <button onClick={fetchBills} className="px-6 py-2 bg-red-100 hover:bg-red-200 rounded-xl font-bold transition-all active:scale-95">
                        Reintentar
                    </button>
                </div>
            )}

            {!loading && !error && (
                <div className="space-y-4">
                    {bills.length === 0 ? (
                        <div className="text-center py-24 bg-white rounded-3xl border border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium">No se registran boletas en este periodo.</p>
                        </div>
                    ) : (
                        bills.map((boleta) => {
                            const isDownloading = downloadingId === boleta.id;
                            const saldoPendiente = boleta.saldo || 0;

                            return (
                                <div key={boleta.id} className="boleta-card group bg-white rounded-3xl p-5 md:p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-sky-200 transition-all duration-300">
                                    <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch">

                                        <div className="flex lg:flex-col justify-between items-start lg:w-48 border-b lg:border-b-0 lg:border-r border-slate-100 pb-4 lg:pb-0 lg:pr-6">
                                            <div className="mb-2">
                                                <p className="text-xl font-black text-slate-800 capitalize leading-none mb-3">
                                                    {formatMonthYear(boleta.fecha_vencimiento || boleta.fecha_emision)}
                                                </p>

                                                <div className="flex flex-col gap-1">
                                                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest flex items-center gap-1">
                                                        <Clock size={10} className="text-amber-500" /> Vencimiento
                                                    </p>
                                                    <p className="text-xs font-bold text-slate-600">
                                                        {formatDateShort(boleta.fecha_vencimiento)}
                                                    </p>
                                                </div>

                                                <p className="text-[10px] font-bold text-slate-400 mt-3 font-mono">#{boleta.folio_valido}</p>
                                            </div>
                                            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter border ${
                                                boleta.desc_estado === 'PAGADO'
                                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                    : 'bg-amber-50 text-amber-600 border-amber-100'
                                            }`}>
                                                {boleta.desc_estado}
                                            </div>
                                        </div>

                                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-6 items-stretch">
                                            <div className="bg-slate-50/50 p-4 rounded-2xl border border-slate-100 flex flex-col justify-center">
                                                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">Monto Factura</p>
                                                <p className="text-lg font-bold text-slate-700">{formatCurrency(boleta.total_factura || 0)}</p>
                                            </div>

                                            <div className="bg-emerald-50/30 p-4 rounded-2xl border border-emerald-50 flex flex-col justify-center">
                                                <p className="text-[10px] text-emerald-500 font-black uppercase tracking-widest mb-1">Abono</p>
                                                <p className="text-lg font-bold text-emerald-700">{formatCurrency(boleta.abono || 0)}</p>
                                            </div>

                                            <div className={`p-4 rounded-2xl border flex flex-col justify-center ${saldoPendiente > 0 ? 'bg-sky-50 border-sky-100' : 'bg-slate-50 border-slate-100'}`}>
                                                <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${saldoPendiente > 0 ? 'text-sky-500' : 'text-slate-400'}`}>Saldo Pendiente</p>
                                                <p className={`text-xl font-black ${saldoPendiente > 0 ? 'text-sky-700' : 'text-slate-400'}`}>{formatCurrency(saldoPendiente)}</p>
                                            </div>
                                        </div>

                                        <div className="flex flex-row lg:flex-col gap-3 justify-center lg:w-40 lg:items-stretch no-print">
                                            <button
                                                onClick={() => handleDownloadPDF(boleta)}
                                                disabled={isDownloading}
                                                className="flex-1 px-4 py-4 bg-sky-600 text-white rounded-2xl font-bold hover:bg-sky-700 transition-all flex items-center justify-center gap-2 text-sm shadow-md shadow-sky-100 active:scale-95 disabled:opacity-70"
                                            >
                                                {isDownloading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                                                PDF
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {cartola && createPortal(
                <div
                    className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-2 sm:p-6"
                    onClick={() => setCartola(null)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Cartola"
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl h-full flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100">
                            <h2 className="text-lg font-black text-slate-800">Cartola</h2>
                            <div className="flex items-center gap-2">
                                <a
                                    href={cartola.url}
                                    download={cartola.name}
                                    className="px-4 py-2 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 transition-all flex items-center gap-2 text-sm"
                                >
                                    <Download size={16} /> Descargar
                                </a>
                                <button
                                    onClick={() => setCartola(null)}
                                    aria-label="Cerrar"
                                    autoFocus
                                    className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-all"
                                >
                                    <X size={22} />
                                </button>
                            </div>
                        </div>
                        <iframe src={cartola.url} title="Cartola" className="flex-1 w-full" />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
